import React, { useState, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { toast } from 'react-toastify';
import { IoSearch } from 'react-icons/io5';
import { RiRefreshLine } from 'react-icons/ri';
import LeaseApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import { parseApiDate, selectStyles, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';

const apiService = new LeaseApiService();
const PAGE_SIZE = 100;

const defaultForm = () => ({
    fromDate: moment().format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
    cc1: null,
    cc2: null,
});

const dateFormatter = (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : '');

const columnDefs = [
    { headerCheckboxSelection: true, checkboxSelection: true, headerCheckboxSelectionFilteredOnly: true, suppressMovable: true, width: 50, pinned: 'left' },
    { headerName: 'Description', field: 'description', filter: true, floatingFilter: true, suppressMovable: true, flex: 2, minWidth: 250 },
    { headerName: 'Ref No', field: 'refNo', filter: true, floatingFilter: true, suppressMovable: true, width: 160 },
    { headerName: 'Ref Date', field: 'refDate', filter: true, floatingFilter: true, suppressMovable: true, width: 140, valueFormatter: dateFormatter },
    { headerName: 'Cost Center 1', field: 'costcenter1', filter: true, floatingFilter: true, suppressMovable: true, width: 180 },
    { headerName: 'Cost Center 2', field: 'costcenter2', filter: true, floatingFilter: true, suppressMovable: true, width: 180 },
    { headerName: 'Status', field: 'status', filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
    { headerName: 'Posted', field: 'isPost', suppressMovable: true, width: 100, valueFormatter: (p) => (p.value ? 'Yes' : 'No') },
];

const LeaseTerminationPosting = () => {
    const [formData, setFormData] = useState(defaultForm());
    const [cc1List, setCc1List] = useState([]);
    const [cc2List, setCc2List] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [selectedRows, setSelectedRows] = useState([]);
    const [searchOpen, setSearchOpen] = useState(true);
    const [posting, setPosting] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const params = new URLSearchParams();
                params.append('dropdownTypes', 'TerminationPostingSearchCC1Names');
                const res = await apiService.getApiParamater('TERMINATION_MULTI', `?${params.toString()}`);
                setCc1List(res?.data?.dropdowns?.TerminationPostingSearchCC1Names || []);
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
        };
        fetchDropdownData();
    }, []);

    const handleCc1Change = async (selectedOption) => {
        setFormData((prev) => ({ ...prev, cc1: selectedOption, cc2: null }));
        setCc2List([]);
        if (!selectedOption) return;
        try {
            const params = new URLSearchParams();
            params.append('DropdownFor', 'TeminationPostingCC2ByCC1Id');
            params.append('CascadValue', selectedOption.value?.value);
            const res = await apiService.getApiParamater('TERMINATION_CASCAD', `?${params.toString()}`);
            setCc2List(res?.data?.items || []);
        } catch (error) {
            console.error('Error fetching cost center 2:', error);
        }
    };

    const clearGrid = () => {
        setRowData([]);
        setPagination(undefined);
        setSelectedRows([]);
    };

    const getItems = async (pageNo = 1) => {
        clearGrid();
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('FromDate', formData.fromDate || '');
            params.append('ToDate', formData.toDate || '');
            // Omit unselected cost centers so the API receives null for them.
            const cc1Text = formData.cc1?.value?.text?.trim();
            const cc2Text = formData.cc2?.value?.text?.trim();
            if (cc1Text) params.append('Costcenter1', cc1Text);
            if (cc2Text) params.append('Costcenter2', cc2Text);
            params.append('PageNo', pageNo || 1);
            params.append('PageSize', pageSize || PAGE_SIZE);
            const res = await apiService.getApiParamater('TERMINATION_POSTING_LIST', `?${params.toString()}`);
            setRowData(res?.data?.result || []);
            setPagination(res?.data?.paginationData);
            setSelectedRows([]);
        } catch (error) {
            console.error('Error fetching termination posting:', error);
            clearGrid();
        } finally {
            setLoading(false);
        }
    };

    const canPost = rowData.length > 0 && selectedRows.length > 0;

    const handlePost = async () => {
        if (!canPost || posting) return;
        const leaseNos = selectedRows.map((row) => row.refNo).filter(Boolean);
        setPosting(true);
        try {
            const res = await apiService.post('POST_LEASE_TERMINATION_POSTING', { leaseNos });
            const summary = res?.data?.result;
            const failed = (summary?.items || []).filter((item) => !item.success);
            if (res?.data?.isSuccessful && !failed.length) {
                toast.success(res?.data?.message || `${summary?.succeededCount ?? leaseNos.length} lease(s) posted`);
            } else {
                failed.forEach((item) => toast.error(`${item.leaseNo}: ${item.result}`));
                if (!failed.length) toast.error(res?.data?.message || 'Posting failed');
                if (summary?.succeededCount) toast.success(`${summary.succeededCount} lease(s) posted`);
            }
            await getItems(1);
        } catch (error) {
            console.error('Error posting termination:', error);
            toast.error(error?.response?.data?.message || 'Posting failed');
        } finally {
            setPosting(false);
        }
    };

    const handleReset = () => {
        setFormData(defaultForm());
        setCc2List([]);
        clearGrid();
    };

    return (
        <div className="p-3">
            <div className="mb-3"><h6>Lease Termination Posting</h6></div>
            <div className="cost-center-section">
                <div className="d-flex cursor-pointer slide-header" onClick={() => setSearchOpen(!searchOpen)}>
                    <div className="param-label pt-1">Search</div>
                </div>
                <div className={`slide-content ${searchOpen ? 'open' : 'closed'}`}>
                    <div className={`border-box2 p-3 ${searchOpen ? 'slide-in' : 'slide-out'}`}>
                        <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
                            <div>
                                <label className="form-label">From Date</label>
                                <DatePicker
                                    selected={parseApiDate(formData.fromDate)}
                                    onChange={(d) => setFormData((prev) => ({ ...prev, fromDate: toApiDate(d) || '' }))}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="form-label">To Date</label>
                                <DatePicker
                                    selected={parseApiDate(formData.toDate)}
                                    onChange={(d) => setFormData((prev) => ({ ...prev, toDate: toApiDate(d) || '' }))}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="form-label">Cost Center 1</label>
                                <Select
                                    className="w-100 p-0"
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={false}
                                    isClearable
                                    styles={selectStyles}
                                    value={formData.cc1}
                                    onChange={handleCc1Change}
                                    options={cc1List
                                        .slice()
                                        .sort((a, b) => (a?.text || '').localeCompare(b?.text || '', undefined, { numeric: true, sensitivity: 'base' }))
                                        .map((rec) => ({ value: rec, label: rec?.text }))}
                                />
                            </div>
                            <div>
                                <label className="form-label">Cost Center 2</label>
                                <Select
                                    className="w-100 p-0"
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={false}
                                    isClearable
                                    styles={selectStyles}
                                    value={formData.cc2}
                                    onChange={(opt) => setFormData((prev) => ({ ...prev, cc2: opt }))}
                                    options={cc2List
                                        .slice()
                                        .sort((a, b) => (a?.text || '').localeCompare(b?.text || '', undefined, { numeric: true, sensitivity: 'base' }))
                                        .map((rec) => ({ value: rec, label: rec?.text }))}
                                />
                            </div>
                            <div className="d-flex gap-2 align-items-end">
                                <div
                                    className="mainbtnGry flex-end gap-2 w-50"
                                    style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.7 : 1 }}
                                    onClick={() => getItems(1)}
                                >
                                    {loading
                                        ? <span className="spinner-border spinner-border-sm text-light center mt-1 mx-2" role="status" />
                                        : <IoSearch color="#fff" className="center mt-1 mx-2" />}
                                    <div className="center">{loading ? 'Searching...' : 'Search'}</div>
                                </div>
                                <div className="mainbtnRed flex-end gap-2 w-50" onClick={handleReset}>
                                    <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="d-flex justify-content-end mt-3">
                <button
                    type="button"
                    className="mainbtn"
                    disabled={!canPost || posting}
                    style={{ width: 180, cursor: canPost && !posting ? 'pointer' : 'not-allowed', opacity: canPost && !posting ? 1 : 0.6 }}
                    onClick={handlePost}
                >
                    {posting ? 'Posting...' : 'Post'}
                </button>
            </div>
            <div className="gridshad mt-2">
                <div className="ag-theme-quartz" style={{ height: 470 }}>
                    <AgGridReact
                        getRowStyle={(params) => ({ background: params.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                        rowData={rowData}
                        loading={loading}
                        columnDefs={columnDefs}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
                    />
                </div>
                <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />
            </div>
        </div>
    );
};

export default LeaseTerminationPosting;
